import { useCallback, useMemo, useState } from 'react';
{
  /* TODO: I guess this pluralization would need to be removed for launchpad integration, but leaving for now */
}

import { pluralize } from '@gonfalon/format/pluralize';
import { pluralizeWord } from '@gonfalon/format/pluralizeWord';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Button } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import clsx from 'clsx';

import { SearchCategoriesContext, SearchResultsContext } from './contexts';
import { SearchCategories } from './SearchCategories';
import { detailsHoverBoundaryProps } from './SearchResults';
import type { ResourceSelectorProps } from './types';

import styles from './ResourceSelector.module.css';

export function ResourceSelector<T extends object = object>({
  selectedKeys,
  onSelectionChange,
  selectionMode,
  onLoadMore,
  hasMore,
  isLoadingMore = false,
  renderDetails,
  renderEmptyState,
  resourceName,
  customSelectedCount,
  onDone = () => {},
  hideDoneButton = false,
  containerClassName,
  containerStyle,
  headerClassName,
  headerStyle,
  hideFooter = false,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  children,
}: ResourceSelectorProps<T>) {
  // Optimized ResourceSelector with hover performance improvements

  const [detailsItem, setDetailsItem] = useState<T | null>(null);

  const handleDetailsItemChange = useCallback((item: T | null) => setDetailsItem(item), []);

  const hasSelections = selectedKeys && (selectedKeys === 'all' || selectedKeys.size > 0);
  const showFooter = !hideFooter && hasSelections && selectionMode === 'multiple';

  const selectedCount = customSelectedCount ?? (selectedKeys === 'all' ? 'All' : (selectedKeys?.size ?? 0));

  // Detect which components are present to apply correct grid layout
  const hasCategories = children.some((child) => child?.type === SearchCategories);
  const isHeader = (child: React.ReactElement) =>
    typeof child?.props === 'object' && child?.props !== null && 'slot' in child.props && child.props.slot === 'header';
  const hasHeader = children.some(isHeader);

  const transformedChildren = children.map((child, index) => {
    if (isHeader(child)) {
      // Re-wrap the children with the headerArea class
      return (
        <div key={`header-${index}`} className={clsx(styles.headerArea, headerClassName)} style={headerStyle}>
          {child.props?.children}
        </div>
      );
    }

    return child;
  });

  // Memoize context values to prevent unnecessary re-renders
  const searchResultsContextValue = useMemo(
    () => ({
      selectedKeys,
      onSelectionChange,
      selectionMode,
      onDetailsItemChange: handleDetailsItemChange,
      renderEmptyState,
      isLoadingMore,
      onLoadMore,
      hasMore,
    }),
    [
      selectedKeys,
      onSelectionChange,
      selectionMode,
      handleDetailsItemChange,
      renderEmptyState,
      isLoadingMore,
      onLoadMore,
      hasMore,
    ],
  );

  const searchCategoriesContextValue = useMemo(() => ({}), []);

  return (
    <SearchResultsContext.Provider value={searchResultsContextValue}>
      <SearchCategoriesContext.Provider value={searchCategoriesContextValue}>
        <div
          className={clsx(
            styles.gridContainer,
            {
              [styles.withCategories]: hasCategories,
              [styles.withDetails]: !!renderDetails,
              [styles.withHeader]: hasHeader,
              [styles.withFooter]: showFooter,
            },
            containerClassName,
          )}
          {...detailsHoverBoundaryProps}
          aria-label={ariaLabel || 'Resource Selector'}
          aria-describedby={ariaDescribedBy}
          style={containerStyle}
        >
          {/* Render all children */}
          {transformedChildren}

          {/* Details panel - memoized to prevent cascade re-renders */}
          {renderDetails && <div className={styles.detailsArea}>{detailsItem && renderDetails(detailsItem)}</div>}

          {/* Footer with unselect all button */}
          {showFooter && (
            <div className={styles.footerArea}>
              <span className={styles.footerAreaText}>
                {' '}
                <Message
                  id="ui.resourceSelector.selectedCount"
                  values={{
                    count: (chunks) => <span className={styles.footerAreaSelectedCount}>{chunks}</span>,
                    selectedCount,
                    resources:
                      (resourceName &&
                        pluralize(
                          selectedCount === 'All' ? 999999 : selectedCount,
                          resourceName,
                          pluralizeWord(resourceName),
                        )) ||
                      '',
                  }}
                />
              </span>
              <Button
                onPress={() => onSelectionChange && onSelectionChange(new Set())}
                className={styles.unselectAllButton}
              >
                <Icon name="cancel" size="small" /> {msg('ui.resourceSelector.unselectAll')}
              </Button>
              {!hideDoneButton && (
                <Button onPress={onDone} variant="primary">
                  {msg('ui.resourceSelector.done')}
                </Button>
              )}
            </div>
          )}
        </div>
      </SearchCategoriesContext.Provider>
    </SearchResultsContext.Provider>
  );
}
